import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { config, API_VERSION, IDENTITY_API_VERSION } from '@/lib/config';
import { storage } from '@/utils/storage';
import { TokenResponse } from '@/types';

const TENANT_IDENTIFIER = config.tenantIdentifier;

// Create axios instances
export const apiClient: AxiosInstance = axios.create({
  baseURL: `${config.apiBaseUrl}/api/${API_VERSION}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': 'ar',
  },
});

export const identityClient: AxiosInstance = axios.create({
  baseURL: `${config.identityApiUrl}/api/${IDENTITY_API_VERSION}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': 'ar',
  },
});

export const storageClient: AxiosInstance = axios.create({
  baseURL: config.storageApiUrl,
  timeout: 60000,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

// Request interceptor to add auth token and impersonation header
const addAuthToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = storage.getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (config.headers) {
    // The unsigned tenant identifier is only valid for anonymous bootstrap and
    // discovery endpoints. Authenticated tenant context comes from the signed
    // JWT (or X-Impersonate-Tenant for SuperAdmin). Sending X-Tenant-Host with
    // authenticated business/admin requests is deliberately rejected by the
    // tenant-resolution middleware.
    config.headers['X-Tenant-Host'] = TENANT_IDENTIFIER;
  }

  // Add impersonation header for SuperAdmin tenant switching
  const stored = localStorage.getItem('maksab_user');
  if (stored) {
    try {
      const state = JSON.parse(stored)?.state;
      if (state?.impersonatedTenantId && config.headers) {
        config.headers['X-Impersonate-Tenant'] = String(state.impersonatedTenantId);
      }
    } catch {
      // ignore parse errors
    }
  }

  return config;
};

// Apply auth interceptor to all clients
[apiClient, identityClient, storageClient].forEach((client) => {
  client.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));
});

// Response interceptor for token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleTokenRefresh = async (originalRequest: InternalAxiosRequestConfig) => {
  const refreshToken = storage.getRefreshToken();

  if (!refreshToken) {
    storage.clearTokens();
    window.location.href = '/login';
    return Promise.reject(new Error('No refresh token'));
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then((token) => {
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${token}`;
      }
      return axios(originalRequest);
    });
  }

  isRefreshing = true;

  try {
    const response = await identityClient.post<TokenResponse>('/auth', {
      GrantType: 'refresh_token',
      RefreshToken: refreshToken,
    });

    const { tokenString, refreshToken: newRefreshToken } = response.data;
    storage.setAccessToken(tokenString);
    storage.setRefreshToken(newRefreshToken);

    processQueue(null, tokenString);

    if (originalRequest.headers) {
      originalRequest.headers.Authorization = `Bearer ${tokenString}`;
    }
    return axios(originalRequest);
  } catch (error) {
    processQueue(error as Error, null);
    storage.clearTokens();
    window.location.href = '/login';
    return Promise.reject(error);
  } finally {
    isRefreshing = false;
  }
};

// Add response interceptor for error handling and token refresh
[apiClient, identityClient].forEach((client) => {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      const hasAuthenticatedSession = Boolean(storage.getAccessToken());

      // A 401 from a public sign-in/OTP request is an authentication result,
      // not an expired dashboard session. Trying to refresh without a stored
      // session redirects the user away from the admin-login form and hides
      // the useful API error. Refresh only requests made by an authenticated
      // browser session.
      if (error.response?.status === 401 && hasAuthenticatedSession && !originalRequest._retry) {
        originalRequest._retry = true;
        return handleTokenRefresh(originalRequest);
      }

      // Handle 403 errors - clear impersonation if it might be the cause
      if (error.response?.status === 403) {
        const stored = localStorage.getItem('maksab_user');
        if (stored) {
          try {
            const state = JSON.parse(stored)?.state;
            if (state?.impersonatedTenantId) {
              console.warn('Clearing impersonated tenant due to 403 error');
              // Clear impersonation and retry once
              const newState = { ...JSON.parse(stored), state: { ...state, impersonatedTenantId: null } };
              localStorage.setItem('maksab_user', JSON.stringify(newState));
              // Don't auto-retry - let the page reload handle it
            }
          } catch {
            // ignore parse errors
          }
        }
      }

      // Handle other errors
      const errorMessage =
        (error.response?.data as { message?: string })?.message ||
        error.message ||
        'حدث خطأ غير متوقع';

      error.message = errorMessage;
      return Promise.reject(error);
    }
  );
});

// Helper function to build query params
export function buildQueryParams(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}
